<section class="open-connections-page">
  <div class="page-header">
    <div>
      <h4>Open Connections</h4>
      <p>Live view of active connections and engagement signals.</p>
    </div>
    <button class="refresh-btn" (click)="refresh()" [disabled]="loading">
      <span
        *ngIf="loading"
        class="spinner-border spinner-border-sm refresh-spinner"
        role="status"
        aria-hidden="true"
      ></span>
      {{ loading ? 'Refreshing...' : 'Refresh' }}
    </button>
  </div>

  <section class="filters-card">
    <div class="filter-item">
      <label class="form-label mb-1">Status Filter</label>
      <select class="form-select form-select-sm" [(ngModel)]="selectedStatus" (change)="onStatusChange()">
        <option *ngFor="let status of statusOptions" [ngValue]="status.value">{{ status.label }}</option>
      </select>
    </div>

    <div class="filter-item">
      <label class="form-label mb-1">Created Date (Preset)</label>
      <select class="form-select form-select-sm" [(ngModel)]="createdDatePreset" (change)="onPresetCreatedDateChange()">
        <option value="">None</option>
        <option *ngFor="let preset of createdDatePresets" [value]="preset.key">
          {{ preset.label }}
        </option>
        <option value="custom">Custom range</option>
      </select>
    </div>

    <div class="filter-item">
      <label class="form-label mb-1">Applied Date Filter</label>
      <div class="date-filter-label">{{ activeDateFilterLabel }}</div>
    </div>

    <div class="filter-item">
      <label class="form-label mb-1">Page Size</label>
      <select class="form-select form-select-sm" [(ngModel)]="pageSize" (change)="onPageSizeChange()">
        <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
      </select>
    </div>

    <div class="filter-actions">
      <button class="btn btn-sm btn-primary" type="button" (click)="applyFilters()">Apply Filters</button>
      <button class="btn btn-sm btn-outline-secondary" type="button" (click)="clearFilters()">Reset</button>
    </div>
  </section>

  <div *ngIf="errorMessage" class="alert alert-warning mb-3">{{ errorMessage }}</div>

  <div *ngIf="!loading && items.length === 0" class="empty-state">
    No open connections found.
  </div>

  <div class="tiles-grid">
    <article class="connection-tile" *ngFor="let item of items">
      <div class="tile-top">
        <h5>{{ item.connectionName || ('Connection #' + item.connectionId) }}</h5>
        <div class="tile-top-right">
          <span class="status-pill" [ngClass]="getConnectionStatusClass(item.connectionStatus)">
            {{ getConnectionStatusLabel(item.connectionStatus) }}
          </span>
          <span class="connection-id">#{{ item.connectionId }}</span>
        </div>
      </div>

      <div class="members-wrap">
        <div class="avatars">
          <button
            type="button"
            class="avatar-btn"
            *ngFor="let member of item.memberNames; let i = index"
            (click)="openImageModal(item, i, member)"
            [title]="'View ' + member"
          >
            <img
              class="avatar"
              [src]="getMemberPicture(item, i)"
              [alt]="member"
              (error)="onImageError($event)"
            />
          </button>
        </div>
        <div class="member-names">{{ item.memberNames.join(', ') }}</div>
      </div>

      <div class="stats-row">
        <div class="activity-meter">
          <div class="activity-ring" [ngStyle]="getActivityStyle(item.activityScore)">
            <div class="activity-inner">{{ getSafeScore(item.activityScore) }}%</div>
          </div>
          <div class="activity-label">Activity Score</div>
        </div>

        <div class="stats-right">
          <div class="meter-chip" [ngClass]="getMeterClass(item.meter)">
            {{ item.meter || 'Unknown' }}
          </div>
          <button
            type="button"
            class="reasoning-btn"
            *ngIf="item.meterReasoning"
            (click)="openMeterReasoningModal(item)"
          >
            Why this meter?
          </button>
          <div class="expiry-line">
            <strong>{{ getExpiryLabel(item.expiryTs) }}:</strong> {{ getExpiryText(item.expiryTs) }}
          </div>
          <div class="expiry-line">
            <strong>Created:</strong> {{ getCreatedText(item.createdTs) }}
          </div>
          <div class="expiry-line">
            <strong>Last activity:</strong> {{ getLastActivityText(item.meterReasoning) }}
          </div>
        </div>
      </div>

      <div class="counts-row">
        <div class="count-box">
          <span>Questions</span>
          <strong>{{ item.totalQuestionsPosted || 0 }}</strong>
        </div>
        <div class="count-box">
          <span>Responses</span>
          <strong>{{ item.totalResponsesPosted || 0 }}</strong>
        </div>
      </div>

      <details class="categories-accordion">
        <summary>Categories ({{ item.categories.length }})</summary>
        <div class="category-list">
          <span class="category-chip" *ngFor="let category of item.categories">
            {{ category }}
          </span>
          <span *ngIf="item.categories.length === 0" class="no-categories">
            No categories yet.
          </span>
        </div>
      </details>
    </article>
  </div>

  <section class="paging-row" *ngIf="totalCount > 0">
    <div class="paging-meta">
      Showing {{ items.length }} of {{ totalCount }} total (Page {{ page }} of {{ totalPages }})
    </div>
    <div class="paging-actions">
      <button class="btn btn-sm btn-outline-secondary" type="button" (click)="goToPreviousPage()" [disabled]="page <= 1 || loading">
        Previous
      </button>
      <button class="btn btn-sm btn-outline-secondary" type="button" (click)="goToNextPage()" [disabled]="page >= totalPages || loading">
        Next
      </button>
    </div>
  </section>

  <div
    class="custom-date-modal-backdrop"
    *ngIf="showCustomDateModal"
    (click)="closeCustomDateModal()"
  >
    <div class="custom-date-modal" (click)="$event.stopPropagation()">
      <div class="custom-date-modal-head">
        <h5>Custom Created Date Range</h5>
        <button type="button" class="custom-date-close" (click)="closeCustomDateModal()">×</button>
      </div>
      <div class="custom-date-grid">
        <div>
          <label class="form-label mb-1">From</label>
          <input type="date" class="form-control form-control-sm" [(ngModel)]="customDateFrom" />
        </div>
        <div>
          <label class="form-label mb-1">To</label>
          <input type="date" class="form-control form-control-sm" [(ngModel)]="customDateTo" />
        </div>
      </div>
      <div class="custom-date-actions">
        <button class="btn btn-sm btn-primary" type="button" (click)="applyCustomDateRange()">Apply</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" (click)="closeCustomDateModal()">Cancel</button>
      </div>
    </div>
  </div>

  <div
    class="image-modal-backdrop"
    *ngIf="selectedImageUrl"
    (click)="closeImageModal()"
  >
    <div class="image-modal" (click)="$event.stopPropagation()">
      <button type="button" class="image-modal-close" (click)="closeImageModal()">×</button>
      <img [src]="selectedImageUrl" [alt]="selectedImageAlt" (error)="onImageError($event)" />
      <div class="image-modal-caption">{{ selectedImageAlt }}</div>
    </div>
  </div>

  <div
    class="reasoning-modal-backdrop"
    *ngIf="selectedMeterTitle"
    (click)="closeMeterReasoningModal()"
  >
    <div class="reasoning-modal" (click)="$event.stopPropagation()">
      <div class="reasoning-modal-head">
        <h5>Meter Reasoning</h5>
        <button type="button" class="reasoning-close" (click)="closeMeterReasoningModal()">×</button>
      </div>
      <div class="reasoning-subtitle">{{ selectedMeterTitle }}</div>

      <div class="reasoning-grid" *ngIf="selectedMeterEntries.length > 0">
        <div class="reasoning-row" *ngFor="let entry of selectedMeterEntries">
          <div class="reasoning-key">{{ entry.label }}</div>
          <div class="reasoning-value">{{ entry.value }}</div>
        </div>
      </div>

      <div class="reasoning-empty" *ngIf="selectedMeterEntries.length === 0">
        {{ selectedMeterRaw || 'No reasoning available.' }}
      </div>
    </div>
  </div>
</section>
